import type { ContractOffer as ContractOrderOffer } from "@frontier/content/contract-orders";
import { useState } from "react";
import { REGIONS } from "@frontier/content/contracts";
import type { ContractSelection } from "@frontier/content/contracts";
import { CUSTOMERS } from "@frontier/content/progression";
import {
  contractAccessTerms,
  contractRewardTerms,
} from "@frontier/content/reward-rules";
import type {
  ContractDifficulty,
  Reputation,
} from "@frontier/content/progression";
import { credits } from "./ui-labels";
import { currentContractDescription } from "./contract-description";
import { passiveInfo } from "./flight-labels";
import "./progression.css";
import { EscortRoutePreview } from "./EscortRoutePreview";
import "./station-pages.css";
import "./contract-roster.css";

interface Props {
  offers: readonly ContractOrderOffer[];
  selected: readonly ContractSelection[];
  reputation: Reputation;
  busy: boolean;
  onChoose: (contracts: ContractSelection[]) => Promise<void>;
}

export function toggleContractSelection(
  selected: readonly ContractSelection[],
  choice: ContractSelection,
): ContractSelection[] | null {
  const existing = selected.find(
    (item) => item.contractId === choice.contractId,
  );
  if (
    existing?.orderId === choice.orderId &&
    existing.difficulty === choice.difficulty
  )
    return selected.filter((item) => item.contractId !== choice.contractId);
  if (existing)
    return selected.map((item) =>
      item.contractId === choice.contractId ? choice : item,
    );
  return selected.length < 3 ? [...selected, choice] : null;
}

export function ContractOffer({
  offer,
  index,
  difficulty,
  selected,
  reputation,
  busy,
  onChoose,
}: Props & {
  offer: ContractOrderOffer;
  index: number;
  difficulty: ContractDifficulty;
}) {
  const id = offer.contractId;
  const contract = offer[difficulty];
  const reward = contractRewardTerms(contract, difficulty);
  const access = contractAccessTerms(contract, difficulty);
  const advancedAccess = contractAccessTerms(offer.advanced, "advanced");
  const customer = reward.reputation?.customerId ?? access?.customerId ?? null;
  const balance = customer ? reputation[customer] : 0;
  const accessBalance = access ? reputation[access.customerId] : 0;
  const advancedBalance = advancedAccess
    ? reputation[advancedAccess.customerId]
    : 0;
  const locked = access !== null && accessBalance < access.minimumReputation;
  const accepted = selected.find((item) => item.contractId === id);
  const chosen =
    accepted?.orderId === offer.orderId && accepted.difficulty === difficulty;
  const anotherRoute =
    accepted !== undefined && accepted.orderId !== offer.orderId;
  const selection = toggleContractSelection(selected, {
    orderId: offer.orderId,
    contractId: id,
    difficulty,
  });
  const region = REGIONS.find((item) => item.id === contract.sectorId);
  return (
    <button
      className={`contract-route ${chosen ? "chosen" : ""} ${locked ? "locked" : ""}`}
      disabled={busy || (locked && !chosen) || selection === null}
      aria-pressed={chosen}
      onClick={() => {
        if (!busy && (!locked || chosen) && selection !== null)
          void onChoose(selection);
      }}
    >
      <span className="route-number">0{index + 1}</span>
      <div className="route-copy">
        <span className="route-sector">
          {region?.name} /{" "}
          {difficulty === "advanced" ? "ПРОДВИНУТЫЙ" : "ОБЫЧНЫЙ"}
        </span>
        <h2>{contract.title}</h2>
        {contract.escort && (
          <EscortRoutePreview
            terms={contract.escort}
            name={contract.title}
            compact
          />
        )}
        {difficulty === "standard" && (
          <p>{currentContractDescription(contract.description)}</p>
        )}
        {customer && (
          <span className="route-customer">
            {CUSTOMERS[customer].name} · {balance} РЕП
            {advancedAccess && advancedAccess.minimumReputation > 0 && (
              <progress
                aria-label={`Репутация: ${CUSTOMERS[advancedAccess.customerId].name}`}
                max={advancedAccess.minimumReputation}
                value={Math.min(
                  advancedBalance,
                  advancedAccess.minimumReputation,
                )}
              />
            )}
          </span>
        )}
        {difficulty === "advanced" && (
          <span className="route-requirements">
            {currentContractDescription(contract.description)}
          </span>
        )}
        {locked && access && (
          <span className="route-lock">
            Нужно {access.minimumReputation} репутации · сейчас {accessBalance}/
            {access.minimumReputation}
          </span>
        )}
        {accepted && !chosen ? (
          <span className="route-accepted">
            {anotherRoute
              ? "Другой маршрут выбран"
              : `Принят ${accepted.difficulty === "advanced" ? "продвинутый" : "обычный"}`}
          </span>
        ) : (
          difficulty === "standard" &&
          advancedAccess !== null &&
          advancedBalance < advancedAccess.minimumReputation && (
            <span className="route-access">
              Продвинутый: ещё{" "}
              {advancedAccess.minimumReputation - advancedBalance} репутации
            </span>
          )
        )}
      </div>
      <div className="route-reward">
        <small className="route-reward-label">НАГРАДА</small>
        <b>
          {BigInt(contract.credits) > 0n
            ? `+${credits(contract.credits)} КР`
            : "ГРУЗ → КР"}
        </b>
        <span>
          +{contract.components} комп.
          {reward.reputation && <> · +{reward.reputation.amount} реп.</>}
        </span>
        {contract.unlock !== "none" && (
          <span>{passiveInfo(contract.unlock).name}</span>
        )}
        <i
          aria-label={
            chosen
              ? "Снять контракт"
              : accepted
                ? anotherRoute
                  ? "Сменить маршрут сопровождения"
                  : "Заменить сложность"
                : "Принять контракт"
          }
        >
          {chosen ? "✓" : locked ? "⌑" : "+"}
        </i>
      </div>
    </button>
  );
}

export function ContractBoard(props: Props) {
  const [difficulty, setDifficulty] = useState<ContractDifficulty>(
    props.selected[0]?.difficulty ?? "standard",
  );
  const accessThresholds = props.offers.map(
    (offer) =>
      contractAccessTerms(offer.advanced, "advanced")?.minimumReputation ?? 0,
  );
  const minimumReputation =
    accessThresholds.length > 0 ? Math.min(...accessThresholds) : null;
  return (
    <section
      className="contract-board contract-roster"
      aria-label="Выбор контрактов"
    >
      <div className="board-caption">
        <div>
          <span className="station-page-kicker">ПОЛЁТНЫЕ ЗАДАЧИ</span>
          <h2>Контракты</h2>
        </div>
        <span>ПОЛНОЕ ВЫПОЛНЕНИЕ → РЕПУТАЦИЯ</span>
      </div>
      <div className="contract-roster-toolbar">
        <div
          className="contract-difficulties"
          role="group"
          aria-label="Сложность контракта"
        >
          <button
            aria-pressed={difficulty === "standard"}
            onClick={() => setDifficulty("standard")}
          >
            ОБЫЧНЫЕ
          </button>
          <button
            aria-pressed={difficulty === "advanced"}
            onClick={() => setDifficulty("advanced")}
          >
            ПРОДВИНУТЫЕ{" "}
            {minimumReputation !== null && (
              <small>ОТ {minimumReputation} РЕП.</small>
            )}
          </button>
        </div>
        <span className="contract-roster-status" role="status">
          Принято {props.selected.length}/3
          {props.selected.length === 0 && (
            <small> · Свободный вылет</small>
          )}
        </span>
      </div>
      <div className="contract-routes">
        {props.offers.map((offer, index) => (
          <ContractOffer
            key={offer.orderId}
            {...props}
            offer={offer}
            index={index}
            difficulty={difficulty}
          />
        ))}
      </div>
    </section>
  );
}
